import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Campaign as CampaignIcon } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import Select from '../components/SafeSelect';
import { useAdmin } from '../context/AdminContext';
import { cloudFunctions } from '../firebase/firebaseConfig';

const PushNotifications = () => {
  const { drivers } = useAdmin();
  const [targetMode, setTargetMode] = useState('all');
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const availableDrivers = useMemo(() => {
    const uniqueDrivers = new Map();

    (drivers || []).forEach((driver) => {
      const driverId = driver?.userId || driver?.uid || driver?.id || '';
      if (!driverId || uniqueDrivers.has(driverId)) {
        return;
      }

      uniqueDrivers.set(driverId, {
        driverId,
        driverName:
          driver?.name ||
          driver?.fullName ||
          driver?.driverName ||
          driver?.email ||
          'Driver',
        email: driver?.email || '',
      });
    });

    return Array.from(uniqueDrivers.values()).sort((firstDriver, secondDriver) =>
      firstDriver.driverName.localeCompare(secondDriver.driverName)
    );
  }, [drivers]);

  const selectedDrivers = useMemo(() => {
    const selectedIds = new Set(selectedDriverIds.map((driverId) => String(driverId)));
    return availableDrivers.filter((driver) => selectedIds.has(String(driver.driverId)));
  }, [availableDrivers, selectedDriverIds]);

  const handleCloseSnackbar = () => {
    setSnackbar((currentSnackbar) => ({ ...currentSnackbar, open: false }));
  };

  const showSnackbar = (nextMessage, severity = 'success') => {
    setSnackbar({
      open: true,
      message: nextMessage,
      severity,
    });
  };

  const handleSendNotifications = async () => {
    const normalizedMessage = String(message || '').trim();
    const normalizedTitle = String(title || '').trim();

    if (!normalizedMessage) {
      showSnackbar('Notification message is required.', 'error');
      return;
    }

    if (targetMode === 'selected' && selectedDriverIds.length === 0) {
      showSnackbar('Select at least one driver.', 'error');
      return;
    }

    try {
      setSending(true);

      const sendAdminPushNotification = httpsCallable(
        cloudFunctions,
        'sendAdminPushNotification'
      );

      const response = await sendAdminPushNotification({
        title: normalizedTitle,
        message: normalizedMessage,
        targetMode,
        driverIds: targetMode === 'selected' ? selectedDriverIds : [],
      });

      const responseData = response?.data || {};
      setResult(responseData);

      showSnackbar(
        `Notification sent to ${responseData.successCount || 0}/${responseData.totalTargets || 0} driver(s).`
      );

      setTitle('');
      setMessage('');
      setTargetMode('all');
      setSelectedDriverIds([]);
    } catch (error) {
      console.error('Failed to send push notifications:', error);
      showSnackbar(error?.message || 'Failed to send push notifications.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Push Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Send app notifications to all drivers or selected drivers.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #eceff1' }}>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Recipients</InputLabel>
              <Select
                label="Recipients"
                value={targetMode}
                disabled={sending}
                onChange={(event) => {
                  setTargetMode(event.target.value);
                  if (event.target.value !== 'selected') {
                    setSelectedDriverIds([]);
                  }
                }}
              >
                <MenuItem value="all">All Drivers ({availableDrivers.length})</MenuItem>
                <MenuItem value="selected">Selected Drivers</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {targetMode === 'selected' && (
            <Grid item size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Select Drivers</InputLabel>
                <Select
                  multiple
                  disabled={sending}
                  value={selectedDriverIds}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedDriverIds(
                      typeof value === 'string' ? value.split(',') : value
                    );
                  }}
                  input={<OutlinedInput label="Select Drivers" />}
                  renderValue={(selectedIds) => {
                    const names = availableDrivers
                      .filter((driver) => selectedIds.includes(driver.driverId))
                      .map((driver) => driver.driverName);
                    return names.join(', ');
                  }}
                >
                  {availableDrivers.map((driver) => (
                    <MenuItem key={driver.driverId} value={driver.driverId}>
                      <Checkbox checked={selectedDriverIds.includes(driver.driverId)} />
                      <ListItemText
                        primary={driver.driverName}
                        secondary={driver.email || driver.driverId}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item size={12}>
            <TextField
              fullWidth
              label="Title (optional)"
              value={title}
              disabled={sending}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Service update"
            />
          </Grid>

          <Grid item size={12}>
            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Message"
              value={message}
              disabled={sending}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Type the notification message for drivers..."
            />
          </Grid>

          <Grid item size={12}>
            <Alert severity="info">
              Drivers without a registered device token will still receive an in-app notification record.
            </Alert>
          </Grid>

          <Grid item size={12}>
            <Button
              variant="contained"
              startIcon={<CampaignIcon />}
              disabled={
                sending ||
                !String(message || '').trim() ||
                (targetMode === 'selected' && selectedDriverIds.length === 0)
              }
              onClick={handleSendNotifications}
            >
              {sending
                ? 'Sending...'
                : targetMode === 'selected'
                  ? `Send to Selected (${selectedDriverIds.length})`
                  : 'Send to All Drivers'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {result && (
        <Paper
          sx={{
            p: 2,
            mt: 2,
            borderRadius: 2,
            border: '1px solid #eceff1',
            bgcolor: '#fafafa',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Last Send Result
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Success: {result.successCount || 0} / {result.totalTargets || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Push Eligible: {result.pushEligibleCount || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Missing Device Token: {result.noTokenCount || 0}
          </Typography>
          {(result.failedCount || 0) > 0 && (
            <Typography variant="body2" sx={{ mt: 1, color: '#d32f2f' }}>
              Failed: {result.failedCount}
            </Typography>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PushNotifications;
